import { forwardRef, useState } from 'react';

import { cva } from 'cva';
import { useFocusable } from 'react-aria/useFocusable';
import { useObjectRef } from 'react-aria/useObjectRef';

import { getContrastingLabelColor } from './utils';

import styles from './avatar.module.css';

export const avatarVariants = cva({
  base: styles.avatar,
  variants: {
    size: {
      xs: styles.size_xs,
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
    },
    variant: {
      circle: styles.circle,
      rectangle: styles.rectangle,
    },
  },
  defaultVariants: {
    size: 'm',
    variant: 'circle',
  },
});

export type AvatarProps = {
  size?: 'xs' | 's' | 'm' | 'l';
  variant?: 'circle' | 'rectangle';
  src?: string;
  alt?: string;
  label: string;
  backgroundColor?: string;
  excludeFromTabOrder?: boolean;
};

/**
 * Avatar component that can show a profile picture or text with an optionally specified background color.
 *
 * Display priority is given to the image if src is provided, followed by the label text.
 * Image display includes alt text if provided.
 *
 * If the image fails to load, the label text is displayed.
 *
 * Label display is optimised for 1-2 characters, and is trimmed to 2 characters on display.
 *
 * The avatar is focusable so it can act as a tooltip trigger. Pass
 * `excludeFromTabOrder` when it is decorative, so it does not become a tab stop.
 *
 * ```tsx
 * <Avatar variant="circle" size="m" src="https://url" alt="John Doe" label="JD" backgroundColor="#FFBDEA" />
 * ```
 */
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      size = 'm',
      variant = 'circle',
      src,
      alt,
      label,
      backgroundColor,
      excludeFromTabOrder,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useObjectRef(forwardedRef);
    const { focusableProps } = useFocusable(
      { ...props, excludeFromTabOrder },
      ref,
    );
    const [isImageError, setIsImageError] = useState(false);

    const overrideStyles = {
      backgroundColor,
      color: backgroundColor && getContrastingLabelColor(backgroundColor),
    };

    const classes = avatarVariants({
      size,
      variant,
    });
    return (
      <div
        role="img"
        ref={ref}
        {...props}
        {...focusableProps}
        className={classes}
        style={overrideStyles}
      >
        {isImageError || !src ? (
          <div>{label.slice(0, 2)}</div>
        ) : (
          <img src={src} alt={alt} onError={() => setIsImageError(true)} />
        )}
      </div>
    );
  },
);
